/* eslint-disable no-prototype-builtins */
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import React from 'react';
import { VariableSizeList } from 'react-window';

const LISTBOX_PADDING = 8; // px

function RenderRow(props) {
  const {
    data, index, style, optionKey, getOptionLabel,
  } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
  };

  if (dataSet.hasOwnProperty('group')) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  const option = dataSet[1];

  return (
    <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
      {typeof getOptionLabel === 'function'
        ? getOptionLabel(option)
        : option?.[optionKey] || ''}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const useStyles = makeStyles({
  paper: {
    boxShadow:
      '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important',
  },
});

const ListboxComponent = React.forwardRef((props, ref) => {
  const {
    children, optionKey, getOptionLabel, ...other
  } = props;
  const itemData = [];

  children.forEach((item) => {
    itemData.push(item);
    if (item.children) {
      itemData.push(...item.children);
    }
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => (child.hasOwnProperty('group') ? 48 : itemSize);

  const getHeight = () => (itemCount > 8 ? 8 * itemSize : itemData.map(getChildSize).reduce((a, b) => a + b, 0));

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {(params) => (
            <RenderRow
              {...params}
              optionKey={optionKey}
              getOptionLabel={getOptionLabel}
            />
          )}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

function VirtualizedAutocomplete({
  value,
  options,
  onSelectedValue,
  multiple,
  textFieldLabel,
  textFieldVariant,
  autoCompleteInputRootStyling,
  textFieldInputRootStyling,
  dropdownStyling,
  inputLabelStyling,
  filterOptions,
  getOptionLabel: getOptionLabelProp,
  placeholder,
  helperText,
  shrink,
  size = 'small',
  limitTags,
  disableClearable,
  optionKey = 'label',
  ...otherProps
}) {
  const classes = useStyles();

  // Safe getOptionLabel that handles null/undefined
  const getOptionLabel = (option) => {
    if (!option) return '';
    if (typeof getOptionLabelProp === 'function') {
      return getOptionLabelProp(option) || '';
    }
    return option?.[optionKey] || '';
  };

  return (
    <Autocomplete
      id="virtualize-autocomplete"
      disableListWrap
      multiple={multiple}
      PopperComponent={StyledPopper}
      ListboxComponent={ListboxComponent}
      ListboxProps={{ optionKey, getOptionLabel }}
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      size={size}
      disableClearable={disableClearable}
      renderInput={(params) => (
        <TextField
          {...params}
          label={textFieldLabel}
          variant={textFieldVariant}
          placeholder={placeholder}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            root: textFieldInputRootStyling,
          }}
          InputLabelProps={{
            classes: {
              root: inputLabelStyling,
            },
            shrink,
          }}
        />
      )}
      renderOption={(props, option, state) => [props, option, state.index]}
      renderGroup={(params) => params}
      value={value}
      limitTags={limitTags}
      classes={{
        inputRoot: autoCompleteInputRootStyling,
        paper: clsx(classes.paper, dropdownStyling),
      }}
      onChange={(event, newValue) => onSelectedValue(newValue)}
      {...otherProps}
    />
  );
}

export default VirtualizedAutocomplete;
